/**
 * S09 · GPS — real `navigator.geolocation` fix, rendered over the
 * showcase's stylised map. "Save pin" writes a GPS capture row and
 * enqueues the sync task.
 */
import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  MapPlaceholder,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import { useGeolocation } from '@/lib/device/useGeolocation';
import { useCreateGpsMutation } from '@kapture/client-core';
import { SubjectChip } from '@/components/SubjectChip';

export const Route = createFileRoute('/sessions/$sessionId/capture/gps')({
  component: GpsCapturePage,
  staticData: { mobile: true },
});

function GpsCapturePage() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const geo = useGeolocation();
  const createGps = useCreateGpsMutation();

  useEffect(() => {
    void geo.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  async function save() {
    if (!geo.point) return;
    const capture = await createGps.mutateAsync({ sessionId, gps: geo.point });
    navigate({
      to: '/sessions/$sessionId/captures/$captureId',
      params: { sessionId, captureId: capture.id },
    });
  }

  const accuracy = geo.point?.accuracyMeters ?? 0;
  const low = accuracy > 12;

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button type="button" onClick={close} aria-label="Close" style={iconBtn}>
          <Ico d={I.close} size={20} />
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: T.font }}>Drop pin</div>
          <SubjectChip tone="light" />
        </div>
        <button
          type="button"
          onClick={() => void geo.request()}
          aria-label="Refresh GPS"
          style={iconBtn}
        >
          <Ico d={I.refresh} size={18} />
        </button>
      </div>

      <div style={{ position: 'relative', flex: 1 }}>
        <MapPlaceholder h="100%" />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
          }}
        >
          <div
            style={{
              width: 160,
              height: 160,
              borderRadius: 80,
              border: `2px solid ${low ? T.warn : T.good}`,
              background: low ? 'rgba(217,119,6,0.1)' : 'rgba(10,148,88,0.1)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%,-100%)',
            }}
          >
            <svg width="32" height="40" viewBox="0 0 32 40">
              <path
                d="M16 2C8 2 2 8 2 16c0 11 14 22 14 22s14-11 14-22c0-8-6-14-14-14z"
                fill={low ? T.warn : T.good}
              />
              <circle cx="16" cy="15" r="5" fill="#fff" />
            </svg>
          </div>
        </div>

        {geo.status === 'requesting' && (
          <Banner tone="info" icon={I.info} title="Locating…" body="Looking for a strong signal." />
        )}
        {geo.status === 'denied' && (
          <Banner
            tone="danger"
            icon={I.warn}
            title="Permission denied"
            body="Enable location for this site in your browser settings and try again."
          />
        )}
        {geo.status === 'unsupported' && (
          <Banner
            tone="warn"
            icon={I.warn}
            title="Geolocation unavailable"
            body="This device doesn't expose GPS through the browser."
          />
        )}
        {geo.status === 'error' && (
          <Banner
            tone="warn"
            icon={I.warn}
            title="Couldn't get a fix"
            body={geo.error ?? 'Tap refresh to retry.'}
          />
        )}
        {geo.status === 'ready' && low && (
          <Banner
            tone="warn"
            icon={I.warn}
            title={`Low accuracy · ±${Math.round(accuracy)}m`}
            body="Weak signal near structure. Hold still or drag the pin."
          />
        )}
      </div>

      <div style={{ padding: 16, background: T.surface, borderTop: `1px solid ${T.stroke}` }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <Field label="Coordinates">
            {geo.point ? `${geo.point.lat.toFixed(5)}, ${geo.point.lng.toFixed(5)}` : '— , —'}
          </Field>
          <Field label="Altitude">
            {geo.point?.altitudeMeters != null ? `${Math.round(geo.point.altitudeMeters)} m` : '—'}
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn
            tone="light"
            size="md"
            icon={<Ico d={I.refresh} size={14} />}
            onClick={() => void geo.request()}
          >
            Refine
          </Btn>
          <Btn
            tone="primary"
            size="md"
            full
            disabled={!geo.point || createGps.isPending}
            onClick={() => void save()}
            style={{
              opacity: !geo.point || createGps.isPending ? 0.5 : 1,
              cursor: !geo.point || createGps.isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {createGps.isPending ? 'Saving…' : 'Save pin'}
          </Btn>
        </div>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

const iconBtn = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: T.surface2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: T.textDim,
  border: 'none',
  cursor: 'pointer',
} as const;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontSize: 10,
          color: T.textMute,
          fontFamily: T.mono,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, fontFamily: T.mono, fontWeight: 500, marginTop: 2 }}>
        {children}
      </div>
    </div>
  );
}

function Banner({
  tone,
  icon,
  title,
  body,
}: {
  tone: 'info' | 'warn' | 'danger';
  icon: string | React.ReactNode;
  title: string;
  body: string;
}) {
  const toneColor = tone === 'danger' ? T.danger : tone === 'warn' ? T.warn : T.info;
  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        padding: '10px 12px',
        background: toneColor,
        color: '#fff',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <Ico d={icon} size={16} stroke="#fff" />
      <div style={{ flex: 1, fontSize: 12, fontFamily: T.font }}>
        <b style={{ fontWeight: 700 }}>{title}</b>
        <div style={{ fontSize: 11, opacity: 0.9 }}>{body}</div>
      </div>
      <Chip tone="hudSolid" size="sm">
        {tone.toUpperCase()}
      </Chip>
    </div>
  );
}
